import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState } from "react";
import { Target, Zap, AlertTriangle, ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";

interface ConditionProtocol {
  id: string;
  name: string;
  badge: string;
  badgeColour: string;
  goals: string[];
  keyInterventions: string[];
  redFlags: string[];
  escalationTriggers: string[];
}

const PROTOCOLS: ConditionProtocol[] = [
  {
    id: "ra",
    name: "Rheumatoid Arthritis (RA)",
    badge: "Inflammatory",
    badgeColour: "bg-red-100 text-red-700",
    goals: [
      "Reduce joint pain and inflammation effects through evidence-based exercise",
      "Maintain and improve joint range of movement (especially hands, wrists, shoulders, feet)",
      "Build muscle strength to support unstable or damaged joints",
      "Improve cardiovascular fitness and reduce metabolic comorbidities",
      "Address fatigue using pacing and energy conservation strategies",
      "Prevent and manage disability — maintain independence in ADLs",
      "Screen for and manage atlantoaxial instability (cervical spine)",
    ],
    keyInterventions: [
      "Aerobic exercise programme: 150 min/week moderate intensity (walking, swimming, cycling)",
      "Progressive resistance exercise 2–3×/week — particularly upper limb and lower limb",
      "Hand therapy: ROM exercises, warm water soaking, splinting if indicated",
      "Hydrotherapy for multi-joint involvement or severe land-based limitations",
      "Energy conservation and joint protection education (OT referral for aids/adaptations)",
      "Fatigue management using pacing and activity scheduling",
      "Cervical spine screen before any cervical manual therapy",
      "Footwear assessment and foot orthoses referral if metatarsalgia/forefoot pain",
    ],
    redFlags: [
      "Sudden onset severe cervical pain with neurological symptoms (tingling, weakness) → suspect AAI, refer urgently",
      "Rapid loss of hand function or new grip weakness",
      "Hot, swollen joint markedly different from others → septic arthritis until proven otherwise",
      "Unexplained constitutional symptoms (fever, weight loss, night sweats) → malignancy or infection",
      "Increasing respiratory symptoms → ILD screening required",
    ],
    escalationTriggers: [
      "BASDAI/disease activity scores significantly worsening despite treatment",
      "New or progressive neurological deficit",
      "Systemic symptoms suggesting infection or disease complication",
      "HAQ-DI deteriorating despite physio input (review with rheumatology)",
      "Falls — refer for comprehensive falls assessment and prevention programme",
    ],
  },
  {
    id: "axspa",
    name: "Axial Spondyloarthritis (axSpA / AS)",
    badge: "Inflammatory",
    badgeColour: "bg-orange-100 text-orange-700",
    goals: [
      "Maintain and improve spinal mobility in all three planes",
      "Prevent progressive kyphotic deformity through daily extension exercise",
      "Reduce pain and morning stiffness",
      "Maintain chest expansion and respiratory function",
      "Improve aerobic fitness and reduce cardiovascular risk",
      "Preserve functional capacity for work and leisure",
    ],
    keyInterventions: [
      "Daily BASEX programme (20–30 min every morning)",
      "Supervised exercise class (physiotherapy-led or BSR-affiliated group) 2–3×/week",
      "Hydrotherapy: 1–2×/week if available — particularly beneficial for thoracic mobility",
      "Postural correction and education — prone lying 20–30 min/day",
      "Breathing exercises and chest expansion exercises daily",
      "Aerobic exercise: swimming and cycling preferred (spine-friendly)",
      "Peripheral joint exercise programme where hips/shoulders involved",
      "Occupational assessment for workplace ergonomics and driving posture",
    ],
    redFlags: [
      "Sudden onset severe neck or back pain after minor trauma → vertebral fracture in ankylosed spine",
      "New neurological signs: leg weakness, numbness, bladder/bowel dysfunction → cauda equina or cord compression",
      "High fever with joint pain and constitutional symptoms → septic arthritis or infection",
      "Rapid new visual disturbance → uveitis (ophthalmology referral same day)",
    ],
    escalationTriggers: [
      "BASDAI ≥ 4 persisting despite 3–6 months of physiotherapy and NSAIDs → biologic assessment pathway",
      "BASMI worsening significantly → medical review of treatment",
      "New or worsening neurological symptoms → urgent imaging and medical review",
      "Functional decline impacting work or self-care despite optimised physiotherapy",
    ],
  },
  {
    id: "sle",
    name: "Systemic Lupus Erythematosus (SLE)",
    badge: "Connective Tissue",
    badgeColour: "bg-purple-100 text-purple-700",
    goals: [
      "Manage fatigue through pacing, aerobic exercise, and energy management",
      "Reduce musculoskeletal pain and joint symptoms",
      "Improve aerobic capacity and reduce cardiovascular risk (elevated in SLE)",
      "Address psychological wellbeing (depression/anxiety prevalence high in SLE)",
      "Maintain muscle strength (steroid myopathy prevention/management)",
      "Support physical function and quality of life",
    ],
    keyInterventions: [
      "Fatigue management programme: activity diary, energy envelope model",
      "Graded aerobic exercise: start 10–15 min 3×/week, build to 150 min/week",
      "Strengthening 2×/week: resist steroid-induced myopathy",
      "Hydrotherapy: low-impact option (indoor UV-protected pool if photosensitive)",
      "Joint protection and pain management education",
      "Mindfulness and relaxation techniques for psychological wellbeing",
      "Photosensitivity precautions: exercise timing and sun-protective clothing",
    ],
    redFlags: [
      "Chest pain, dyspnoea on exertion → pleuritis, pericarditis, or PAH — urgent referral",
      "Sudden severe headache or visual disturbance → CNS lupus or CAPS (antiphospholipid) — emergency",
      "New unexplained oedema and haematuria → lupus nephritis flare",
      "Severe new weakness → myopathy, myositis, or CNS involvement",
      "Post-exertional collapse or significant malaise lasting > 48h → review activity grading",
    ],
    escalationTriggers: [
      "Fatigue not improving despite 8–12 weeks of structured physio — refer to fatigue clinic",
      "Falls or significant weakness → occupational therapy and medical review",
      "New or worsening organ involvement symptoms → rheumatology urgent review",
      "SpO2 dropping during exercise → respiratory/cardiac assessment required",
    ],
  },
  {
    id: "ssc",
    name: "Systemic Sclerosis (SSc)",
    badge: "Connective Tissue",
    badgeColour: "bg-blue-100 text-blue-700",
    goals: [
      "Maintain hand and digit mobility (slow progression of contractures)",
      "Maintain facial and jaw mobility (microstomia management)",
      "Preserve thoracic expansion and respiratory function",
      "Manage Raynaud's with thermal regulation strategies during exercise",
      "Maintain upper limb function and independence",
    ],
    keyInterventions: [
      "Hand exercise programme 2–3×/day (warm water soaking first)",
      "Facial exercises (jaw stretching, lip exercises) twice daily",
      "Thoracic mobility exercises and incentive spirometry (if ILD)",
      "Hydrotherapy with careful temperature control (35–36°C, warm pool)",
      "Occupational therapy: aids and adaptations for hand dysfunction",
      "Raynaud's management: exercise planning around thermal protection",
      "Refer to specialist SSc physiotherapy service if available",
    ],
    redFlags: [
      "Digital ischaemia or gangrenous change → vascular surgical review urgently",
      "Significant dyspnoea at low exertion → ILD/PAH — urgent respiratory/cardiology review",
      "Sudden severe chest pain → pericarditis or PAH crisis — emergency",
      "Dysphagia worsening rapidly → SLT referral and GI review",
    ],
    escalationTriggers: [
      "SpO2 dropping below 94% during exercise → respiratory review",
      "Significant hand contracture preventing self-care → OT and hand surgery review",
      "Digital ulcers not healing → wound care and vascular review",
      "PAH confirmed → specialist PAH centre input; exercise prescription requires cardiology guidance",
    ],
  },
  {
    id: "myopathy",
    name: "Inflammatory Myopathy (PM/DM/IBM)",
    badge: "Myositis",
    badgeColour: "bg-teal-100 text-teal-700",
    goals: [
      "Improve proximal muscle strength (hip flexors, shoulder abductors, knee extensors)",
      "Improve functional capacity and activities of daily living",
      "Reverse deconditioning safely with graded exercise",
      "Maintain aerobic capacity and reduce cardiovascular risk",
      "Address falling risk (proximal weakness significant falls risk)",
    ],
    keyInterventions: [
      "Graded exercise therapy (GET): start 40–50% 1RM, progress 10% per 2 weeks",
      "Functional training: sit-to-stand, stair-climbing, reaching tasks",
      "Aerobic conditioning: 10–30 min cycling/walking 3×/week",
      "Falls prevention programme (high priority)",
      "Respiratory physiotherapy if respiratory muscle weakness suspected",
      "Dysphagia management in collaboration with SLT if swallowing affected",
    ],
    redFlags: [
      "Sudden respiratory deterioration → myositis ILD — emergency medical review",
      "Dysphagia with aspiration risk → SLT urgent referral",
      "Cardiac dysrhythmia or chest pain → myocarditis — emergency",
      "Rapid severe muscle weakness → acute myositis flare — immunosuppression review",
    ],
    escalationTriggers: [
      "CK rising significantly on repeat testing despite exercise programme → medical review",
      "Post-exertional worsening of weakness 48h after sessions → reduce intensity, reassess",
      "Falls with injury → urgent occupational therapy and medical review",
      "Respiratory muscle weakness (FVC declining) → respiratory physiotherapy and medical review",
    ],
  },
  {
    id: "gout",
    name: "Gout (Acute and Intercritical)",
    badge: "Crystal",
    badgeColour: "bg-yellow-100 text-yellow-700",
    goals: [
      "Acute phase: protect joint, reduce pain, maintain joint position",
      "Intercritical phase: lifestyle modification and activity resumption",
      "Long-term: cardiovascular fitness, weight management, reduce metabolic risk",
    ],
    keyInterventions: [
      "Acute phase: rest, elevation, ice (if tolerated), immobilisation if severe",
      "Intercritical phase: gradual resumption of exercise within 48–72h of resolution",
      "Weight management programme: low-impact aerobic exercise (walking, swimming, cycling)",
      "Dietary advice signposting (purine-rich foods, alcohol) — in collaboration with dietetics",
      "Cardiovascular risk reduction exercise programme",
    ],
    redFlags: [
      "Fever with acutely inflamed joint → septic arthritis — emergency, joint aspiration required",
      "Multiple joint involvement simultaneously → polyarticular gout or sepsis",
      "Skin breakdown over tophus → wound infection risk",
    ],
    escalationTriggers: [
      "Acute attack not resolving within 7–10 days → medical review",
      "Increasing frequency of attacks despite urate-lowering therapy → medication review",
      "Large tophi interfering with function → rheumatology/orthopaedic review",
    ],
  },
  {
    id: "cppd",
    name: "Calcium Pyrophosphate Deposition Disease (CPPD)",
    badge: "Crystal",
    badgeColour: "bg-yellow-100 text-yellow-700",
    goals: [
      "Acute phase: pain management, joint protection, and restoration of function",
      "Intercritical phase: maintain joint range of movement and strength",
      "Long-term: reduce falls risk, maintain independence, address co-existing OA",
      "Identify and address metabolic triggers (hyperparathyroidism, haemochromatosis, hypomagnesaemia)",
    ],
    keyInterventions: [
      "Acute phase: rest, joint elevation, ice (if tolerated), gentle ROM within pain limits",
      "Post-acute: graded mobilisation; quadriceps and hip strengthening for knee involvement",
      "Aerobic exercise once acute resolved: swimming, cycling, hydrotherapy",
      "Education: explanation of condition, difference from RA, role of physiotherapy",
      "Falls prevention: particularly important in elderly; CPPD disproportionately affects older patients",
      "Hydrotherapy for multi-joint involvement or where land exercise limited",
    ],
    redFlags: [
      "Fever with acutely inflamed joint → septic arthritis until proven otherwise — emergency",
      "Multiple simultaneous joint flares → consider systemic illness, pseudo-gout polyarticular, or infection",
      "Neurological symptoms with cervical CPPD (crowned dens syndrome) → imaging and medical review",
      "Rapid joint destruction → consider surgical review",
    ],
    escalationTriggers: [
      "Acute attack not resolving within 10–14 days → medical review; consider aspiration and corticosteroid injection",
      "Recurrent acute episodes (>3/year) → metabolic screen referral (PTH, Mg2+, ferritin, Fe studies)",
      "Significant functional decline post-acute episode → occupational therapy and physiotherapy review",
      "Crowned dens syndrome (atlantoaxial CPPD with neurological signs) → urgent spinal imaging",
    ],
  },
  {
    id: "oa",
    name: "OA Overlap in Rheumatology",
    badge: "Degenerative",
    badgeColour: "bg-slate-100 text-slate-600",
    goals: [
      "Reduce pain and improve physical function",
      "Increase quadriceps strength and hip abductor strength",
      "Improve aerobic fitness",
      "Support weight management",
      "Improve self-management and confidence",
    ],
    keyInterventions: [
      "Quadriceps and hip strengthening 3×/week (NICE NG226 OA guideline)",
      "Aerobic exercise 150 min/week: walking, cycling, hydrotherapy",
      "Joint protection advice and pacing education",
      "Weight management support: refer to dietitian and exercise programme",
      "Orthoses: knee bracing or foot orthoses where appropriate",
      "Self-management: ESCAPE-pain or equivalent group programme",
    ],
    redFlags: [
      "Sudden severe worsening of joint pain → acute crystal deposition, fracture, or septic arthritis",
      "Systemic symptoms with joint pain → inflammatory arthritis or malignancy",
      "Joint locking or giving way → mechanical derangement — orthopaedic review",
    ],
    escalationTriggers: [
      "Significant functional decline despite 3 months of physio → surgical referral consideration (NICE NG226)",
      "Uncontrolled pain despite conservative management → pain service review",
    ],
  },
];

function ProtocolCard({ protocol }: { protocol: ConditionProtocol }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${protocol.badgeColour}`}>{protocol.badge}</span>
          <h3 className="font-bold text-slate-900">{protocol.name}</h3>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-emerald-600" />
                <h4 className="font-semibold text-slate-900 text-sm">Physiotherapy Goals</h4>
              </div>
              <ul className="space-y-1.5">
                {protocol.goals.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-2" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-blue-600" />
                <h4 className="font-semibold text-slate-900 text-sm">Key Interventions</h4>
              </div>
              <ul className="space-y-1.5">
                {protocol.keyInterventions.map((k, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-blue-400 shrink-0">→</span>
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <h4 className="font-semibold text-slate-900 text-sm">Red Flags</h4>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-2">
                {protocol.redFlags.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-red-900">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                    {r}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight className="h-4 w-4 text-amber-600" />
                <h4 className="font-semibold text-slate-900 text-sm">Escalation Triggers</h4>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2">
                {protocol.escalationTriggers.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-amber-900">
                    <ArrowUpRight className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                    {e}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConditionProtocols() {
  return (
    <PhysioLayout title="Condition-Specific Protocols">
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h2 className="font-bold text-slate-900 mb-1">Rheumatology Physiotherapy Management Summaries</h2>
          <p className="text-sm text-slate-600">Concise per-condition physiotherapy management guides covering goals, key interventions, red flags, and escalation triggers. Each protocol reflects UK guidelines (NICE, BSR, CSP) and ACPR rheumatology physiotherapy recommendations. Use alongside full clinical assessment.</p>
        </div>

        <div className="space-y-3">
          {PROTOCOLS.map((protocol) => (
            <ProtocolCard key={protocol.id} protocol={protocol} />
          ))}
        </div>
      </div>
    </PhysioLayout>
  );
}
